import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux';
import { Spin } from 'antd';
import {
	AUTH_PREFIX_PATH,
	UNAUTHENTICATED_ENTRY,
	REDIRECT_URL_KEY,
	APP_PREFIX_PATH
} from 'configs/AppConfig'
import navigationConfig from 'configs/NavigationConfig';
import permissionService from 'services/PermissionService';
import tokenStorage from 'utils/tokenStorage';
import useManagerPlanEntitlements, { shouldFetchPlanForRole } from 'hooks/useManagerPlanEntitlements';
import { navItemAllowedByPlan } from 'utils/navPlanGate';

/** Route NavigationConfig la plus spécifique (path le plus long) pour ce pathname. */
const findMatchingNavItem = (pathname) => {
	let bestMatch = null;

	const visit = (items) => {
		items.forEach(item => {
			if (item.path && pathname.startsWith(item.path)) {
				if (!bestMatch || item.path.length > bestMatch.path.length) {
					bestMatch = item;
				}
			}
			if (item.submenu && item.submenu.length > 0) {
				visit(item.submenu);
			}
		});
	};

	visit(navigationConfig);
	return bestMatch;
};

/** Pas dans la sidebar : accès via FAB uniquement ; mêmes droits que les écrans flux agent IA. */
const WORKFLOW_TESTER_PATH = `${APP_PREFIX_PATH}/voice-agent/workflow-tester`;

const ProtectedRoute = () => {
	const { role } = useSelector(state => state.auth)
	const location = useLocation()
	const { plan, loading: planEntitlementsLoading } = useManagerPlanEntitlements();

	// JWT = cookie httpOnly ; présence session = rôle Redux ou métadonnée user_role (refresh)
	const sessionRole = tokenStorage.getItem('user_role');
	const isAuthenticated = !!(role || sessionRole);

	if (!isAuthenticated) {
		return <Navigate to={`${AUTH_PREFIX_PATH}${UNAUTHENTICATED_ENTRY}?${REDIRECT_URL_KEY}=${location.pathname}`} replace />;
	}

	const normalizedRole = (role || sessionRole || '').toLowerCase();
	const path = location.pathname;
	let navMatch = findMatchingNavItem(path);
	if (path === WORKFLOW_TESTER_PATH || path.startsWith(`${WORKFLOW_TESTER_PATH}/`)) {
		navMatch = {
			path: WORKFLOW_TESTER_PATH,
			permissions: ['FLOWS_VIEW'],
			planRequires: 'agentIa'
		};
	}
	const requiredPermissions = navMatch && Array.isArray(navMatch.permissions) ? navMatch.permissions : [];
	const planReady = !shouldFetchPlanForRole(normalizedRole) || !planEntitlementsLoading;

	// Protection par rôle sur tous les dashboards
	if (path.startsWith(`${APP_PREFIX_PATH}/dashboards/admin`) && normalizedRole !== 'admin') {
		return <Navigate to={`${AUTH_PREFIX_PATH}/error-page-1`} replace />;
	}
	if (path.startsWith(`${APP_PREFIX_PATH}/dashboards/user`) && !(normalizedRole === 'manager' || normalizedRole === 'staff')) {
		return <Navigate to={`${AUTH_PREFIX_PATH}/error-page-1`} replace />;
	}
	if (path.startsWith(`${APP_PREFIX_PATH}/dashboards/reports`) && !(normalizedRole === 'manager' || normalizedRole === 'staff')) {
		return <Navigate to={`${AUTH_PREFIX_PATH}/error-page-1`} replace />;
	}
	if (path.startsWith(`${APP_PREFIX_PATH}/client/dashboard`) && normalizedRole !== 'client') {
		return <Navigate to={`${AUTH_PREFIX_PATH}/error-page-1`} replace />;
	}
	if (path.startsWith(`${APP_PREFIX_PATH}/agent/`) && normalizedRole !== 'agent') {
		return <Navigate to={`${AUTH_PREFIX_PATH}/error-page-1`} replace />;
	}
	// Interdire l'ancien accès aux stats client par URL (manager/staff/etc.)
	if (path.startsWith(`${APP_PREFIX_PATH}/productivity/client-stats`)) {
		return <Navigate to={`${AUTH_PREFIX_PATH}/error-page-1`} replace />;
	}

	// Vérification des permissions basées sur NavigationConfig
	if (requiredPermissions.length > 0 && !permissionService.hasAnyPermission(requiredPermissions)) {
		return <Navigate to={`${AUTH_PREFIX_PATH}/error-page-1`} replace />;
	}

	// Plan (dialer, agent IA, etc.) : attendre le chargement pour ne pas afficher puis retirer
	if (navMatch?.planRequires && shouldFetchPlanForRole(normalizedRole)) {
		if (!planReady) {
			return (
				<div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<Spin size="large" />
				</div>
			);
		}
		if (!navItemAllowedByPlan(navMatch, plan, true)) {
			return <Navigate to={`${APP_PREFIX_PATH}/dashboards/user`} replace />;
		}
	}

	return <Outlet />
}

export default ProtectedRoute
